import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { useFinance } from '@/hooks/useFinance';
import { expenseCategoryMeta, formatDate, formatMoney, monthlySeries } from '@/lib/finance';
import { exportTableCsv, exportTableExcel, exportTablePdf, type ExportTable } from '@/lib/financeExport';
import { FinanceSection, FinanceStatCard } from '../components/FinanceUI';

interface ProductRow {
  id: number;
  name: string;
  price: number | null;
  cost_price: number | null;
  discount_enabled: boolean;
  discount_price: number | null;
}

export default function FinanceRapportsPage() {
  const finance = useFinance();
  const { user } = useAuth();
  const { currency, transactions, expenses, accounts, accountBalances, invoices, suppliers, loading } = finance;
  const [msg, setMsg] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductRow[]>([]);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('product_items')
      .select('id, name, price, cost_price, discount_enabled, discount_price')
      .eq('idcommerce', getCommerceId(user))
      .order('name')
      .then(({ data }) => setProducts((data || []) as ProductRow[]));
  }, [user]);

  const profitability = useMemo(() => {
    return products.map((p) => {
      const sale = p.discount_enabled && p.discount_price != null ? Number(p.discount_price) : Number(p.price) || 0;
      const cost = p.cost_price != null ? Number(p.cost_price) : null;
      const margin = cost != null ? sale - cost : null;
      const marginPct = cost != null && sale > 0 ? (margin! / sale) * 100 : null;
      return { id: p.id, name: p.name, sale, cost, margin, marginPct };
    });
  }, [products]);

  const missingCost = profitability.filter((p) => p.cost == null).length;

  const series = useMemo(() => monthlySeries(transactions, expenses, 6), [transactions, expenses]);

  const revenueTotal = transactions.filter((t) => t.direction === 'in').reduce((s, t) => s + Number(t.amount), 0);
  const expenseTotal = expenses.reduce((s, e) => s + Number(e.amount), 0);

  const reports: { key: string; label: string; icon: string; desc: string; build: () => ExportTable }[] = [
    {
      key: 'revenus', label: 'Rapport de revenus', icon: 'ri-money-dollar-circle-line', desc: `${transactions.filter((t) => t.direction === 'in').length} entrée(s)`,
      build: () => ({ title: 'Rapport de revenus', headers: ['Date', 'Catégorie', 'Description', 'Client', `Montant (${currency})`],
        rows: transactions.filter((t) => t.direction === 'in').map((t) => [formatDate(t.occurred_at), t.category || '', t.description || '', t.counterparty || '', Number(t.amount)]) }),
    },
    {
      key: 'depenses', label: 'Rapport de dépenses', icon: 'ri-shopping-bag-3-line', desc: `${expenses.length} sortie(s)`,
      build: () => ({ title: 'Rapport de dépenses', headers: ['Date', 'Catégorie', 'Description', 'Fournisseur', `Montant (${currency})`],
        rows: expenses.map((e) => [formatDate(e.occurred_at), expenseCategoryMeta(e.category).label, e.description || '', e.vendor || '', Number(e.amount)]) }),
    },
    {
      key: 'tresorerie', label: 'Rapport de trésorerie', icon: 'ri-bank-line', desc: `${accounts.length} compte(s)`,
      build: () => ({ title: 'Rapport de trésorerie', headers: ['Compte', 'Type', `Solde (${currency})`],
        rows: accounts.map((a) => [a.name, a.account_type, accountBalances[a.id] || 0]) }),
    },
    {
      key: 'rentabilite', label: 'Rapport de rentabilité', icon: 'ri-line-chart-line', desc: '6 derniers mois',
      build: () => ({ title: 'Rapport de rentabilité', headers: ['Mois', 'Revenus', 'Dépenses', 'Bénéfice'],
        rows: series.map((s) => [s.key, s.revenue, s.expense, s.profit]) }),
    },
    {
      key: 'creances', label: 'Créances clients', icon: 'ri-user-received-line', desc: `${finance.credit.customers.length} client(s)`,
      build: () => ({ title: 'Créances clients', headers: ['Client', 'Téléphone', `Solde (${currency})`],
        rows: finance.credit.customers.map((c) => [c.full_name, c.phone || '', finance.credit.balances[c.id] || 0]) }),
    },
    {
      key: 'dettes', label: 'Dettes fournisseurs', icon: 'ri-truck-line', desc: `${suppliers.length} fournisseur(s)`,
      build: () => ({ title: 'Dettes fournisseurs', headers: ['Fournisseur', 'Téléphone', `Solde (${currency})`],
        rows: suppliers.map((s) => [s.name, s.phone || '', finance.supplierBalance(s.id)]) }),
    },
    {
      key: 'factures', label: 'Rapport de factures', icon: 'ri-file-text-line', desc: `${invoices.length} facture(s)`,
      build: () => ({ title: 'Rapport de factures', headers: ['Numéro', 'Client', 'Statut', 'Émission', 'Échéance', 'Total', 'Payé'],
        rows: invoices.map((i) => [i.number, i.customer_name || '', i.status, formatDate(i.issue_date), i.due_date ? formatDate(i.due_date) : '', i.total, i.amount_paid]) }),
    },
    {
      key: 'mensuel', label: 'Rapport mensuel complet', icon: 'ri-calendar-line', desc: 'Synthèse du mois',
      build: () => {
        const now = new Date();
        const monthTx = transactions.filter((t) => { const d = new Date(t.occurred_at); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
        const monthEx = expenses.filter((e) => { const d = new Date(e.occurred_at); return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear(); });
        return {
          title: 'Rapport mensuel', subtitle: now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
          headers: ['Indicateur', `Valeur (${currency})`],
          rows: [
            ['Revenus', monthTx.filter((t) => t.direction === 'in').reduce((s, t) => s + Number(t.amount), 0)],
            ['Dépenses', monthEx.reduce((s, e) => s + Number(e.amount), 0)],
            ['Trésorerie disponible', Object.values(accountBalances).reduce((s, v) => s + v, 0)],
            ['Créances clients', finance.credit.stats.totalOutstanding],
            ['Dettes fournisseurs', suppliers.reduce((s, sup) => s + Math.max(0, finance.supplierBalance(sup.id)), 0)],
          ],
        };
      },
    },
  ];

  const run = (r: { label: string; build: () => ExportTable }, format: 'csv' | 'xlsx' | 'pdf') => {
    const table = r.build();
    if (format === 'csv') exportTableCsv(table, table.title);
    else if (format === 'xlsx') exportTableExcel(table, table.title);
    else exportTablePdf(table, table.title);
    setMsg(`« ${r.label} » exporté au format ${format === 'xlsx' ? 'Excel' : format.toUpperCase()}.`);
    window.setTimeout(() => setMsg(null), 3000);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <FinanceStatCard label="Revenus (total)" value={formatMoney(revenueTotal, currency)} icon="ri-arrow-down-circle-line" tone="primary" />
        <FinanceStatCard label="Dépenses (total)" value={formatMoney(expenseTotal, currency)} icon="ri-arrow-up-circle-line" tone="accent" />
        <FinanceStatCard label="Bénéfice estimé" value={formatMoney(revenueTotal - expenseTotal, currency)} icon="ri-line-chart-line" tone={revenueTotal - expenseTotal >= 0 ? 'secondary' : 'danger'} />
        <FinanceStatCard label="Trésorerie" value={formatMoney(Object.values(accountBalances).reduce((s, v) => s + v, 0), currency)} icon="ri-wallet-3-line" tone="secondary" />
      </div>

      {msg && <div className="px-4 py-2.5 rounded-lg bg-primary-50 text-primary-800 text-sm flex items-center gap-2"><i className="ri-check-line"></i>{msg}</div>}

      <FinanceSection title="Générer un rapport (CSV · Excel · PDF)" icon="ri-download-2-line">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {reports.map((r) => (
            <div key={r.key} className="flex flex-col gap-2 p-3.5 bg-background-50 border border-background-200/70 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
                  <i className={`${r.icon} text-lg`}></i>
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground-900">{r.label}</span>
                  <span className="block text-xs text-foreground-400">{r.desc}</span>
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => run(r, 'csv')} className="flex-1 px-2 py-1.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer">CSV</button>
                <button onClick={() => run(r, 'xlsx')} className="flex-1 px-2 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium whitespace-nowrap hover:bg-primary-100 transition-colors cursor-pointer">Excel</button>
                <button onClick={() => run(r, 'pdf')} className="flex-1 px-2 py-1.5 rounded-full bg-accent-50 text-accent-700 text-xs font-medium whitespace-nowrap hover:bg-accent-100 transition-colors cursor-pointer">PDF</button>
              </div>
            </div>
          ))}
        </div>
      </FinanceSection>

      <FinanceSection title="Rentabilité par produit" icon="ri-line-chart-line">
        {missingCost > 0 && (
          <p className="text-[11px] text-accent-700 bg-accent-50 rounded-lg px-3 py-2 mb-3 flex items-start gap-1.5">
            <i className="ri-information-line mt-0.5"></i>
            {missingCost} produit(s) sans coût d’achat : la marge est incomplète pour ces produits. Ajoutez le coût d’achat dans la fiche produit.
          </p>
        )}
        {profitability.length === 0 ? (
          <p className="text-sm text-foreground-400">Aucun produit dans votre catalogue.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-foreground-400 border-b border-background-200/70">
                  <th className="py-2 pr-3 font-medium">Produit</th>
                  <th className="py-2 px-3 font-medium text-right">Coût</th>
                  <th className="py-2 px-3 font-medium text-right">Prix de vente</th>
                  <th className="py-2 px-3 font-medium text-right">Marge unitaire</th>
                  <th className="py-2 pl-3 font-medium text-right">Marge %</th>
                </tr>
              </thead>
              <tbody>
                {profitability.map((p) => (
                  <tr key={p.id} className="border-b border-background-200/40">
                    <td className="py-2 pr-3 text-foreground-800 truncate max-w-[220px]">{p.name}</td>
                    <td className="py-2 px-3 text-right text-foreground-600">{p.cost != null ? formatMoney(p.cost, currency) : '—'}</td>
                    <td className="py-2 px-3 text-right text-foreground-800">{formatMoney(p.sale, currency)}</td>
                    <td className={`py-2 px-3 text-right font-medium ${p.margin == null ? 'text-foreground-400' : p.margin >= 0 ? 'text-primary-700' : 'text-red-600'}`}>{p.margin != null ? formatMoney(p.margin, currency) : '—'}</td>
                    <td className={`py-2 pl-3 text-right font-semibold ${p.marginPct == null ? 'text-foreground-400' : p.marginPct >= 0 ? 'text-foreground-900' : 'text-red-600'}`}>{p.marginPct != null ? `${p.marginPct.toFixed(1)} %` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </FinanceSection>

      <FinanceSection title="Synthèse 6 derniers mois" icon="ri-table-line">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-foreground-400 border-b border-background-200/70">
                <th className="py-2 pr-3 font-medium">Mois</th>
                <th className="py-2 px-3 font-medium text-right">Revenus</th>
                <th className="py-2 px-3 font-medium text-right">Dépenses</th>
                <th className="py-2 pl-3 font-medium text-right">Bénéfice</th>
              </tr>
            </thead>
            <tbody>
              {series.map((s) => (
                <tr key={s.key} className="border-b border-background-200/40">
                  <td className="py-2 pr-3 text-foreground-700 capitalize">{s.label}</td>
                  <td className="py-2 px-3 text-right text-primary-700 font-medium">{formatMoney(s.revenue, currency)}</td>
                  <td className="py-2 px-3 text-right text-accent-700 font-medium">{formatMoney(s.expense, currency)}</td>
                  <td className={`py-2 pl-3 text-right font-semibold ${s.profit >= 0 ? 'text-foreground-900' : 'text-red-600'}`}>{formatMoney(s.profit, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </FinanceSection>
    </div>
  );
}